import { scaleTime, scaleUtc } from "d3-scale";
import { timeDay, utcDay, utcHour, utcMinute } from "d3-time";
import { timeFormat, utcFormat } from "d3-time-format";

export type BucketStep = "fiveMinutes" | "hour" | "sixHours" | "day";

export interface ChartSeries {
  key: string;
  label: string;
  /** The keys of what the series adds up, when it stands for several. */
  members?: readonly string[];
}

/** One bucket's value for each series, in the order of the series. */
export interface ChartBucket<Value> {
  start: string;
  values: readonly Value[];
}

export const chartWidth = 600;
export const chartHeight = 180;
export const chartMargin = { top: 8, right: 8, bottom: 24, left: 48 };

/**
 * Hues far apart, with neighbours unlike in both hue and lightness so stacked series stay distinct.
 * Class names are written out whole so Tailwind finds them.
 */
const seriesShades = [
  { fill: "fill-sky-500", stroke: "stroke-sky-500", swatch: "bg-sky-500" },
  { fill: "fill-amber-400", stroke: "stroke-amber-400", swatch: "bg-amber-400" },
  { fill: "fill-violet-500", stroke: "stroke-violet-500", swatch: "bg-violet-500" },
  { fill: "fill-emerald-400", stroke: "stroke-emerald-400", swatch: "bg-emerald-400" },
  { fill: "fill-rose-500", stroke: "stroke-rose-500", swatch: "bg-rose-500" },
  { fill: "fill-cyan-300", stroke: "stroke-cyan-300", swatch: "bg-cyan-300" },
  { fill: "fill-orange-600", stroke: "stroke-orange-600", swatch: "bg-orange-600" },
  { fill: "fill-lime-300", stroke: "stroke-lime-300", swatch: "bg-lime-300" },
] as const;

/** The most series one chart names. Past that, the rest are added together as everything else. */
export const MOST_SERIES = seriesShades.length;

/** Rows by how much moved through them, largest first: the order of stacks, legends, and tables. */
export function largestFirst<Row>(rows: readonly Row[], size: (row: Row) => bigint) {
  return [...rows].sort((left, right) => {
    const difference = size(right) - size(left);
    return difference > 0n ? 1 : difference < 0n ? -1 : 0;
  });
}

/** The `count` largest rows by `size`, whatever their sign, left in the order they came in. */
export function keepLargest<Row>(
  rows: readonly Row[],
  count: number,
  size: (row: Row) => number | bigint,
) {
  const sizes = rows.map(row => {
    const value = size(row);
    return value < 0 ? -value : value;
  });
  const ranked = rows
    .map((_, index) => index)
    .sort((left, right) => {
      const leftSize = sizes[left] ?? 0;
      const rightSize = sizes[right] ?? 0;
      return rightSize > leftSize ? 1 : rightSize < leftSize ? -1 : 0;
    });
  const kept = new Set(ranked.slice(0, count));
  return {
    kept: rows.filter((_, index) => kept.has(index)),
    rest: rows.filter((_, index) => !kept.has(index)),
  };
}

export const EVERYTHING_ELSE: ChartSeries = { key: "everything-else", label: "Everything else" };

/**
 * Everything else is hatched in bars and dashed in lines, in a hue no series has, so it never reads
 * as one of them. A series pulled out of it for a moment takes its hue, solid.
 */
export const everythingElseShade = {
  hatch: "stroke-pink-400",
  fill: "fill-pink-400",
  stroke: "stroke-pink-400",
  swatch:
    "text-pink-400 bg-[repeating-linear-gradient(135deg,currentColor_0_1.5px,transparent_1.5px_4px)] ring-1 ring-inset ring-current",
  dash: "border-t-2 border-dashed border-pink-400",
};

/** One folded series in a hover breakdown, its value already written out. */
export interface FoldedItem {
  key: string;
  label: string;
  value: string;
}

export const shadeOf = (index: number) =>
  seriesShades[index % seriesShades.length] ?? seriesShades[0];

const sixHours = utcHour.every(6) ?? utcHour;
const fiveMinutes = utcMinute.every(5) ?? utcMinute;

export const bucketInterval = { fiveMinutes, hour: utcHour, sixHours, day: utcDay } as const;

/** Day buckets are whole UTC days, so they place and read in UTC; the rest in the viewer's time. */
export const timeScale = (step: BucketStep) => (step === "day" ? scaleUtc() : scaleTime());

const formatUtcHour = utcFormat("%H:%M");
const formatUtcDay = utcFormat("%b %-d");
const formatLocalHour = timeFormat("%H:%M");
const formatLocalDay = timeFormat("%b %-d");
const localDayAndHour = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});
const localHourAndZone = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZoneName: "short",
});
const localDayHourAndZone = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZoneName: "short",
});

/** An axis label: the date at midnight, the time at any other hour, in the zone the step reads in. */
export function formatTimeTick(date: Date, step: BucketStep) {
  if (step === "day") return utcDay(date) < date ? formatUtcHour(date) : formatUtcDay(date);
  return timeDay(date) < date ? formatLocalHour(date) : formatLocalDay(date);
}

export function formatBucketStart(start: Date, step: BucketStep) {
  if (step === "day") return `${formatUtcDay(start)} (UTC day)`;
  if (step === "hour") return localDayHourAndZone.format(start);
  return formatSpan(start, bucketInterval[step].offset(start, 1));
}

/** A span within a day or so, in the viewer's time: the day and hour it starts, the hour it ends. */
export const formatSpan = (from: Date, to: Date) =>
  `${localDayAndHour.format(from)}–${localHourAndZone.format(to)}`;

const formatUtcDayInYear = utcFormat("%b %-d, %Y");
const formatUtcMonth = utcFormat("%B %Y");

/**
 * The period a chart shows, `to` not included: whole UTC days or a calendar month for day buckets,
 * the viewer's day and hour otherwise. The year shows only when it isn't this one.
 */
export function formatPeriod(from: Date, to: Date, step: BucketStep, month = false) {
  if (month) return formatUtcMonth(from);
  if (step !== "day") return `${localDayAndHour.format(from)} – ${localDayAndHour.format(to)}`;
  const thisYear = new Date().getUTCFullYear();
  const day = (date: Date) =>
    date.getUTCFullYear() === thisYear ? formatUtcDay(date) : formatUtcDayInYear(date);
  return `${day(from)} – ${day(utcDay.floor(new Date(to.valueOf() - 1)))}`;
}

/** Lets an id from `useId` sit inside an SVG `url(#…)` reference. */
export const svgId = (id: string) => id.replace(/[^a-zA-Z0-9_-]/g, "");

const chartCredits = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/** Display credits placed on a chart, to two decimals, so a sliver below zero reads 0 rather than -0. */
export const formatChartCredits = (credits: number) => {
  const rounded = Math.round(credits * 100) / 100;
  return chartCredits.format(rounded === 0 ? 0 : rounded);
};
